import { PalmettoButton, PalmettoIcon, PalmettoSelect } from '@palmetto/dsm-components';
import { DateInput } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import type { Control } from 'react-hook-form';

import { ApprovedVendorAllocation, avlTimelineStart } from 'types';
import type { AvlAllocationDetail, IsoDateString } from 'types';

import { ApprovedVendorAllocationLabels } from './allocationLabels';
import { AllocationScheduleSection } from './AllocationScheduleSection';
import type { AvlFormValues } from './ApprovedVendorItemForm';

const addIcon = <PalmettoIcon name="add" size="sm" />;

const allocationStatusOptions = Object.values(ApprovedVendorAllocation).map((value) => ({
  value,
  label: ApprovedVendorAllocationLabels[value],
}));

const toIsoDateString = (date: Date): IsoDateString => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}` as IsoDateString;
};

export interface PendingAllocationWindow {
  startDate: string;
  allocationStatus: ApprovedVendorAllocation | '';
}

export interface CompletePendingWindow {
  startDate: IsoDateString;
  allocationStatus: ApprovedVendorAllocation;
}

export const isCompletePendingWindow = (row: PendingAllocationWindow): row is CompletePendingWindow =>
  Boolean(row.startDate) && Boolean(row.allocationStatus);

// Blank rows are ignored and dropped on submit.
export const validatePendingWindows = (
  existingLatestStartDate: string | undefined,
  pending: PendingAllocationWindow[],
): (string | undefined)[] => {
  const errors: (string | undefined)[] = [];
  let previousStart = existingLatestStartDate;

  for (const row of pending) {
    if (!row.startDate && !row.allocationStatus) {
      errors.push(undefined);
      continue;
    }
    if (!row.startDate || !row.allocationStatus) {
      errors.push('Start date and status are both required.');
      continue;
    }
    if (row.startDate < avlTimelineStart) {
      errors.push(`Start date must be on or after ${avlTimelineStart}.`);
      continue;
    }
    if (previousStart && row.startDate <= previousStart) {
      errors.push(`Start date must be later than the previous window's start (${previousStart}).`);
      continue;
    }
    // Only a valid row's start becomes the floor for the next one.
    errors.push(undefined);
    previousStart = row.startDate;
  }

  return errors;
};

interface PendingAllocationRowProps {
  index: number;
  control: Control<AvlFormValues>;
  remove: (index: number) => void;
  error?: string;
  disabled?: boolean;
}

const PendingAllocationRow = ({ index, control, remove, error, disabled }: PendingAllocationRowProps) => {
  const handleRemove = useCallback(() => remove(index), [remove, index]);

  return (
    <div className="flex flex-col gap-4">
      {/* flex-1 wrappers give the select real width so its longest label ("Pre-Approval") doesn't overflow. */}
      <div className="flex items-end gap-8">
        <div className="min-w-0 flex-1">
          <Controller
            name={`newAllocationDetails.${index}.startDate`}
            control={control}
            render={({ field }) => {
              const selected = field.value ? new Date(`${field.value}T00:00:00`) : undefined;
              return (
                <DateInput
                  id={`new-allocation-start-date-${index}`}
                  name={`new-allocation-start-date-${index}`}
                  label="Start Date"
                  placeholder="MM-DD-YYYY"
                  value={selected ? selected.toLocaleDateString('en-US') : ''}
                  isRequired
                  datePickerProps={{
                    onChange: (date: Date | [Date, Date] | null) => {
                      const picked = Array.isArray(date) ? date[0] : date;
                      field.onChange(picked ? toIsoDateString(picked) : '');
                    },
                    selected,
                  }}
                  textInputProps={{}}
                  disabled={disabled}
                />
              );
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <Controller
            name={`newAllocationDetails.${index}.allocationStatus`}
            control={control}
            render={({ field }) => (
              <PalmettoSelect
                label="Allocation Status"
                items={allocationStatusOptions.map((opt) => ({ ...opt, selected: opt.value === field.value }))}
                onValueChange={(details: { value: string[] }) =>
                  field.onChange((details.value[0] as ApprovedVendorAllocation) ?? '')
                }
                disabled={disabled}
              />
            )}
          />
        </div>
        <PalmettoButton
          type="button"
          emphasis="secondary"
          layout="hug"
          aria-label={`Remove pending window ${index + 1}`}
          onClick={handleRemove}
          disabled={disabled}
        >
          <PalmettoIcon name="trash" size="sm" />
        </PalmettoButton>
      </div>
      {error && (
        <div role="alert" className="body-200 text-text-error">
          {error}
        </div>
      )}
    </div>
  );
};

export interface AllocationScheduleFormSectionProps {
  control: Control<AvlFormValues>;
  existingAllocationDetails: AvlAllocationDetail[] | undefined;
  /** Floor the first pending window validates against. */
  existingLatestStartDate: string | undefined;
  disabled?: boolean;
  serverError?: string;
}

export const AllocationScheduleFormSection = ({
  control,
  existingAllocationDetails,
  existingLatestStartDate,
  disabled,
  serverError,
}: AllocationScheduleFormSectionProps) => {
  const { fields, append, remove } = useFieldArray({ control, name: 'newAllocationDetails' });
  const pendingValues = useWatch({ control, name: 'newAllocationDetails' }) ?? [];

  // Not memoized: `useWatch` returns a new array each render, so the memo would never hit.
  const errors = validatePendingWindows(existingLatestStartDate, pendingValues);

  const handleAdd = useCallback(() => append({ startDate: '', allocationStatus: '' }), [append]);

  return (
    <div className="mb-16 flex flex-col gap-8">
      <AllocationScheduleSection allocationDetails={existingAllocationDetails} />

      {fields.map((field, index) => (
        <PendingAllocationRow
          key={field.id}
          index={index}
          control={control}
          remove={remove}
          error={errors[index]}
          disabled={disabled}
        />
      ))}

      <div className="flex items-center justify-between">
        <PalmettoButton
          type="button"
          emphasis="secondary"
          layout="hug"
          leadingIcon={addIcon}
          onClick={handleAdd}
          disabled={disabled}
        >
          Add Allocation
        </PalmettoButton>
        <span className="body-300 text-text-secondary">Saved windows can&apos;t be edited or removed.</span>
      </div>

      {serverError && (
        <div role="alert" className="body-200 text-text-error">
          {serverError}
        </div>
      )}
    </div>
  );
};
